import React, { Component } from 'react';
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { lowerCaseFirstLetter } from 'ps-components/lib/formatters';
import * as salaryRangeActionCreators from './SalaryRangeActions';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import MarketPricingTable from './MarketPricingTable/MarketPricingTable';
import RoundingInput from './RoundingInput/RoundingInput';
import SalaryRangeChart from './SalaryRangeChart/SalaryRangeChart';
import SalaryRangeFooter from './SalaryRangeFooter/SalaryRangeFooter';
import SalaryRangeHeader from './SalaryRangeHeader/SalaryRangeHeader';
import SalaryRangeSettings from './SalaryRangeSettings/SalaryRangeSettings';
import './SalaryRangeContainer.scss';

const BENCHMARKS = ['base_salary_25th', 'base_salary_50th', 'base_salary_75th'];

class SalaryRangeContainer extends Component {
  static propTypes = {
    actions: shape({
      getJobByID: func,
      getJobData: func,
      getMarketElements: func,
      updateJobRange: func
    }),
    hideSlideout: func,
    jobMarketElements: arrayOf(
      shape({
        field_title: string,
        field_value: number,
        source_field: string
      })
    ),
    jobRange: shape({
      salaryRange: shape({
        max: number,
        mid: number,
        min: number
      }),
      salaryRangePct: shape({
        max: number,
        mid: number,
        min: number
      }),
      salaryRangePctElements: shape({
        max: string,
        mid: string,
        min: string
      })
    }),
    marketElements: shape({
      payElements: arrayOf(
        shape({
          field_title: string,
          source_field: string
        })
      )
    }),
    maxSalaryRange: shape({
      currentSalary: number,
      element: string,
      elementDisplay: string,
      percent: number
    }),
    midSalaryRange: shape({
      currentSalary: number,
      element: string,
      elementDisplay: string,
      percent: number
    }),
    minSalaryRange: shape({
      currentSalary: number,
      element: string,
      elementDisplay: string,
      percent: number
    }),
    roundTo: number,
    selectedJob: shape({
      code: string,
      codeKey: string,
      id: string,
      title: string
    }),
    showSlideout: func,
    slideout: shape({
      open: bool,
      slideoutProps: shape({
        orgJobId: number
      }),
      slideoutType: object
    })
  };

  state = {
    currentMaxValue: 0,
    currentMidValue: 0,
    currentMinValue: 0,
    maxElement: '',
    maxElementDisplay: '',
    maxPct: 0,
    maxValue: 0,
    midElement: '',
    midElementDisplay: '',
    midPct: 0,
    midValue: 0,
    minElement: '',
    minElementDisplay: '',
    minPct: 0,
    minValue: 0,
    roundTo: 1,
    tempMaxValue: '',
    tempMinValue: '',
    tempMidValue: ''
  };

  componentDidMount() {
    const { getJobByID } = this.props.actions;
    const { slideoutProps } = this.props.slideout;
    getJobByID(slideoutProps.orgJobId);
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      actions,
      jobMarketElements,
      jobRange,
      maxSalaryRange,
      minSalaryRange,
      midSalaryRange,
      roundTo,
      selectedJob,
      slideout
    } = this.props;

    const hasSelectedJob = Object.keys(selectedJob).length !== 0;
    const hadSelectedJob = Object.keys(prevProps.selectedJob).length !== 0;

    if (hasSelectedJob && !hadSelectedJob) {
      actions.getMarketElements();
      actions.getJobData(selectedJob);
    }

    if (prevProps.jobRange === null && jobRange !== prevProps.jobRange) {
      this.setState({
        currentMaxValue: Math.round(jobRange.salaryRange.max),
        currentMidValue: Math.round(jobRange.salaryRange.mid),
        currentMinValue: Math.round(jobRange.salaryRange.min),
        roundTo: roundTo
      });
    }

    if (prevProps.jobMarketElements === null && jobMarketElements !== prevProps.jobMarketElements) {
      let maxRangeValue, midRangeValue, minRangeValue;

      if (jobMarketElements.length) {
        maxRangeValue = this.findProposedRangeValue(maxSalaryRange.elementDisplay);
        midRangeValue = this.findProposedRangeValue(midSalaryRange.elementDisplay);
        minRangeValue = this.findProposedRangeValue(minSalaryRange.elementDisplay);
      }

      this.setState({
        maxElement: maxSalaryRange.element,
        maxElementDisplay: maxSalaryRange.elementDisplay,
        maxPct: maxSalaryRange.percent,
        maxValue: maxRangeValue || this.state.currentMaxValue,
        midElement: midSalaryRange.element,
        midElementDisplay: midSalaryRange.elementDisplay,
        midPct: midSalaryRange.percent,
        midValue: midRangeValue || this.state.currentMidValue,
        minElement: minSalaryRange.element,
        minElementDisplay: minSalaryRange.elementDisplay,
        minPct: minSalaryRange.percent,
        minValue: minRangeValue || this.state.currentMinValue
      });
    }

    if (!prevProps.slideout.open && slideout.open && slideout.slideoutProps.orgJobId) {
      const { getJobByID } = this.props.actions;
      const { slideoutProps } = this.props.slideout;
      getJobByID(slideoutProps.orgJobId);
    }

    if (!slideout.open && prevProps.slideout.open) {
      actions.clearSelectedJob();
      this.setState({ roundTo: 1 });
    }
  }

  closeSlideout = () => {
    const { actions, hideSlideout } = this.props;
    const { maxElement, maxPct, midElement, midPct, minElement, minPct } = this.state;
    const { maxSalaryRange, midSalaryRange, minSalaryRange } = this.props;

    if (maxElement !== maxSalaryRange.element)
      this.setState({ maxElement: maxSalaryRange.element, maxElementDisplay: maxSalaryRange.elementDisplay });
    if (midElement !== midSalaryRange.element)
      this.setState({ midElement: midSalaryRange.element, midElementDisplay: midSalaryRange.elementDisplay });
    if (minElement !== minSalaryRange.element)
      this.setState({ minElement: minSalaryRange.element, minElementDisplay: minSalaryRange.elementDisplay });
    if (maxPct !== maxSalaryRange.percent) this.setState({ maxPct: maxSalaryRange.percent });
    if (midPct !== midSalaryRange.percent) this.setState({ midPct: midSalaryRange.percent });
    if (minPct !== minSalaryRange.percent) this.setState({ minPct: minSalaryRange.percent });
    this.setState({
      tempMaxValue: '',
      tempMinValue: '',
      tempMidValue: ''
    });
    hideSlideout();
    actions.clearSelectedJob();
  };

  findElementDisplayValue = value => {
    const { jobMarketElements } = this.props;
    const hasJobMarketElements = jobMarketElements.find(element => element.field_title === value);
    return hasJobMarketElements ? hasJobMarketElements.field_value : 0;
  };

  findElementValue = value => {
    const { marketElements } = this.props;
    return marketElements.payElements.find(element => element.field_title === value).source_field;
  };

  findProposedRangeValue = value => {
    const { jobMarketElements } = this.props;

    return jobMarketElements.find(element => element.field_title === value).field_value;
  };

  getMarketPricing = () => {
    const { jobMarketElements } = this.props;
    const marketPricingElements = jobMarketElements.filter(({ source_field }) => BENCHMARKS.includes(source_field));

    return marketPricingElements.reduce((memo, element) => {
      memo[element.source_field] = element.field_value;
      return memo;
    }, {});
  };

  getNumberWithNoCommas = value => {
    return value.replace(/[$,]+/g, '');
  };

  getPercentOfTotal = (pct, total) => {
    const { roundTo } = this.state;
    const value = (pct / 100) * total;
    const roundedValue = Math.round(value / roundTo) * roundTo;
    return roundedValue;
  };

  getStateToChange = (value, type, status = '') => {
    const isMid = value.id.includes('mid');
    const isMin = value.id.includes('min');
    return isMin ? `${status}Min${type}` : isMid ? `${status}Mid${type}` : `${status}Max${type}`;
  };

  setElementValue = (event, valueToChange) => {
    const value = this.findElementDisplayValue(event.label);

    this.setState({
      [valueToChange.concat('Element')]: this.findElementValue(event.label),
      [valueToChange.concat('ElementDisplay')]: event.label,
      [valueToChange.concat('Value')]: value ? value : 0
    });
  };

  setProposedRangeValue = event => {
    const target = event.target;
    const numberWithNoCommas = this.getNumberWithNoCommas(target.value);
    const tempValueToChange = this.getStateToChange(target, 'Value', 'temp');
    let pctToChange = this.getStateToChange(target, 'Pct');
    let valueToChange = this.getStateToChange(target, 'Value');
    let targetElement = this.getStateToChange(target, 'ElementDisplay');

    pctToChange = lowerCaseFirstLetter(pctToChange);
    valueToChange = lowerCaseFirstLetter(valueToChange);
    targetElement = lowerCaseFirstLetter(targetElement);
    let isDisabled = targetElement ? !this.findProposedRangeValue(this.state[targetElement]) : true;
    const newPercent = (parseInt(numberWithNoCommas) / this.state[valueToChange]) * 100;

    if (isDisabled) {
      this.setState({
        [pctToChange]: 100,
        [valueToChange]: numberWithNoCommas,
        [tempValueToChange]: ''
      });
    } else {
      this.setState({
        [pctToChange]: newPercent,
        [tempValueToChange]: ''
      });
    }
  };

  setRangePctValue = event => {
    const target = event.target;
    const isMid = target.id.includes('mid');
    const isMin = target.id.includes('min');
    const newValue = target.value % 1 === 0 ? parseInt(target.value) : parseFloat(target.value);
    const pctToChange = isMin ? 'minPct' : isMid ? 'midPct' : 'maxPct';

    this.setState({ [pctToChange]: newValue });
  };

  setRoundToValue = event => {
    const newPercent = parseInt(event.replace(/[$,]+/g, ''));

    this.setState({ roundTo: newPercent });
  };

  setTempProposedRangeValue = event => {
    const target = event.target;
    const valueToChange = this.getStateToChange(target, 'Value', 'temp');
    const numberWithNoCommas = this.getNumberWithNoCommas(target.value);

    this.setState({ [valueToChange]: numberWithNoCommas });
  };

  updateRange = () => {
    const { actions, selectedJob } = this.props;
    const { maxPct, maxValue, midPct, midValue, minPct, minValue, roundTo } = this.state;

    actions.updateJobRange(selectedJob, this.state, roundTo);

    this.setState({
      currentMaxValue: this.getPercentOfTotal(maxPct, maxValue),
      currentMidValue: this.getPercentOfTotal(midPct, midValue),
      currentMinValue: this.getPercentOfTotal(minPct, minValue)
    });
  };

  render() {
    const { jobMarketElements, jobRange, marketElements, selectedJob } = this.props;

    const {
      currentMaxValue,
      currentMidValue,
      currentMinValue,
      maxElementDisplay,
      maxPct,
      maxValue,
      midElementDisplay,
      midPct,
      midValue,
      minElementDisplay,
      minPct,
      minValue,
      roundTo,
      tempMaxValue,
      tempMinValue,
      tempMidValue
    } = this.state;

    if (!jobRange || !jobMarketElements) {
      return (
        <div className="salary__range-slideout">
          <InPlaceIndicator />
        </div>
      );
    }

    const marketData = this.getMarketPricing();
    const proposedMaxValue = currentMaxValue === maxValue ? currentMaxValue : this.getPercentOfTotal(maxPct, maxValue);
    const proposedMinValue = currentMinValue === minValue ? currentMinValue : this.getPercentOfTotal(minPct, minValue);
    const proposedMidValue = currentMidValue === midValue ? currentMidValue : this.getPercentOfTotal(midPct, midValue);

    return (
      <div className="salary__range-slideout">
        <SalaryRangeHeader closeSlideout={this.closeSlideout} jobCodeKey={selectedJob.code} title={selectedJob.title} />
        <div className="salary__range-panels">
          <section className="range__panel-left">
            <SalaryRangeSettings
              findProposedRangeValue={this.findProposedRangeValue}
              getPercentOfTotal={this.getPercentOfTotal}
              marketElements={marketElements.payElements}
              maxElementDisplay={maxElementDisplay}
              maxPct={maxPct}
              maxValue={maxValue}
              minElementDisplay={minElementDisplay}
              minPct={minPct}
              minValue={minValue}
              midElementDisplay={midElementDisplay}
              midPct={midPct}
              midValue={midValue}
              roundTo={roundTo}
              setElementValue={this.setElementValue}
              setProposedRangeValue={this.setProposedRangeValue}
              setTempProposedRangeValue={this.setTempProposedRangeValue}
              setRangePctValue={this.setRangePctValue}
              tempMaxValue={tempMaxValue}
              tempMinValue={tempMinValue}
              tempMidValue={tempMidValue}
            />
          </section>
          <section className="range__panel-right">
            <SalaryRangeChart
              currentMaxValue={currentMaxValue}
              currentMidValue={currentMidValue}
              currentMinValue={currentMinValue}
              proposedMaxValue={proposedMaxValue}
              proposedMidValue={proposedMidValue}
              proposedMinValue={proposedMinValue}
              roundTo={roundTo}
            />
            <MarketPricingTable data={marketData} />
            <RoundingInput onClick={this.setRoundToValue} value={roundTo} />
          </section>
        </div>
        <SalaryRangeFooter closeSlideout={this.closeSlideout} updateRange={this.updateRange} />
      </div>
    );
  }
}

const mapStateToProps = ({ salaryRangeSlideout }) => {
  return { ...salaryRangeSlideout };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ ...salaryRangeActionCreators }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(SalaryRangeContainer);
